import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Droplets, ArrowLeft } from 'lucide-react';

const TerminosCondiciones = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Términos y Condiciones | Aqua Nexia</title>
        <meta name="description" content="Términos y condiciones de uso de Aqua Nexia." />
      </Helmet>

      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900">Aqua Nexia</span>
          </Link>
          <Link to="/" className="text-sm text-sky-600 hover:text-sky-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-8">Términos y Condiciones</h1>
        <p className="text-sm text-slate-500 mb-8">Última actualización: Febrero 2026</p>

        <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">1. Definiciones</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>"Servicio":</strong> la plataforma SaaS Aqua Nexia accesible en aquanexia.com.</li>
              <li><strong>"Usuario":</strong> cualquier persona que accede y utiliza el Servicio.</li>
              <li><strong>"Propietario":</strong> usuario con rol de gestión de la empresa (owner).</li>
              <li><strong>"Trabajador":</strong> usuario con rol operativo (worker), invitado por un Propietario.</li>
              <li><strong>"Suscripción":</strong> plan de pago contratado para acceder al Servicio.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">2. Descripción del Servicio</h2>
            <p>
              Aqua Nexia es una plataforma de gestión integral para empresas de mantenimiento de piscinas que incluye:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li>Gestión de clientes, trabajadores y trabajos programados.</li>
              <li>Generación de partes de trabajo digitales y reportes en PDF.</li>
              <li>Control de inventario y materiales.</li>
              <li>Facturación: presupuestos, albaranes y facturas.</li>
              <li>Asistente de inteligencia artificial.</li>
              <li>Calendario y optimización de rutas.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">3. Registro y cuenta de usuario</h2>
            <p>Para utilizar el Servicio, el usuario debe:</p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li>Registrarse proporcionando datos veraces y actualizados.</li>
              <li>Ser mayor de 18 años o contar con autorización legal.</li>
              <li>Mantener la confidencialidad de sus credenciales de acceso.</li>
              <li>Notificar inmediatamente cualquier uso no autorizado de su cuenta.</li>
            </ul>
            <p className="mt-3">
              El usuario es responsable de toda la actividad que ocurra en su cuenta.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">4. Planes y precios</h2>
            <p>
              Aqua Nexia ofrece diferentes planes de suscripción (Autónomo, PYME, Empresa) con facturación 
              mensual o anual. Los precios vigentes se publican en la página de precios del Sitio Web.
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li>Todos los planes incluyen un período de prueba gratuito de 14 días.</li>
              <li>Los precios se expresan en euros e incluyen el IVA aplicable.</li>
              <li>El pago se procesa de forma segura a través de Stripe.</li>
              <li>Las suscripciones se renuevan automáticamente al final de cada período.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">5. Período de prueba</h2>
            <p>
              Al registrarse, el usuario dispone de 14 días de prueba gratuita con acceso completo a las 
              funcionalidades del plan elegido. Transcurrido el período de prueba, se realizará el primer 
              cobro automáticamente. El usuario puede cancelar en cualquier momento durante la prueba sin coste alguno.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">6. Cancelación y reembolsos</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>El usuario puede cancelar su suscripción en cualquier momento desde el panel de configuración.</li>
              <li>La cancelación surte efecto al final del período de facturación en curso.</li>
              <li>No se realizan reembolsos prorrateados por períodos parciales.</li>
              <li>Las suscripciones anuales no son reembolsables una vez transcurridos los primeros 14 días.</li>
              <li>Tras la cancelación, se mantendrá acceso limitado durante 30 días para exportar datos.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">7. Uso aceptable</h2>
            <p>El usuario se compromete a:</p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li>No utilizar el Servicio para fines ilegales o no autorizados.</li>
              <li>No intentar acceder a datos de otros usuarios o empresas.</li>
              <li>No interferir con el funcionamiento normal del Servicio.</li>
              <li>No compartir su cuenta con personas no autorizadas.</li>
              <li>Cumplir con la normativa de protección de datos respecto a los datos de sus clientes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">8. Propiedad de los datos</h2>
            <p>
              Los datos introducidos por el usuario en la plataforma (clientes, trabajos, reportes, facturas) 
              son propiedad del usuario. Aqua Nexia actúa como encargado del tratamiento de dichos datos.
            </p>
            <p>
              El usuario puede exportar sus datos en cualquier momento utilizando las funciones de exportación 
              disponibles en la plataforma.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">9. Disponibilidad del Servicio</h2>
            <p>
              Aqua Nexia se esfuerza por mantener una disponibilidad del 99.9%. No obstante, pueden producirse 
              interrupciones por mantenimiento programado o causas de fuerza mayor. Se notificará con antelación 
              cuando sea posible.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">10. Limitación de responsabilidad</h2>
            <p>
              Aqua Nexia no será responsable de daños indirectos, incidentales, especiales o consecuentes derivados 
              del uso del Servicio. La responsabilidad total estará limitada al importe pagado por el usuario en los 
              últimos 12 meses.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">11. Modificaciones</h2>
            <p>
              Aqua Nexia se reserva el derecho de modificar estos Términos. Los cambios se notificarán con al 
              menos 30 días de antelación por correo electrónico. El uso continuado del Servicio tras los cambios 
              implica la aceptación de los nuevos Términos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">12. Legislación aplicable</h2>
            <p>
              Estos Términos se rigen por la legislación española. Para la resolución de conflictos, las partes 
              se someten a los Juzgados y Tribunales de [ciudad], salvo que la normativa de consumo otorgue otra 
              jurisdicción al usuario.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">13. Contacto</h2>
            <p>
              Para cualquier consulta sobre estos Términos, puede contactarnos en <strong>info@aquanexia.com</strong>.
            </p>
          </section>
        </div>
      </main>

      <footer className="py-8 px-4 border-t mt-12">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
          <Link to="/aviso-legal" className="hover:text-sky-600">Aviso Legal</Link>
          <span>·</span>
          <Link to="/politica-privacidad" className="hover:text-sky-600">Privacidad</Link>
          <span>·</span>
          <Link to="/terminos-condiciones" className="hover:text-sky-600">Términos</Link>
          <span>·</span>
          <Link to="/politica-cookies" className="hover:text-sky-600">Cookies</Link>
        </div>
      </footer>
    </div>
  );
};

export default TerminosCondiciones;
